import compose from "recompose/compose";
import withState from "recompose/withState";
import withHandlers from "recompose/withHandlers";
import withProps from "recompose/withProps";
import setOptionHandler from "./utils/setOptionHandler";
import computeUpdatedPricesForProduct from "./computeUpdatedPrices";

// A direct input is an option for which Customers can provide any value (text…)
const isDirectInput = (option) => option?.values?.length <= 0;

const samePrice = (price) => price;

const makePriceAdjuster = (option) => {
  const { extraCost } = option;
  switch (extraCost.type) {
    case "currency":
      return (price) => {
        return price + extraCost.prices.priceInclTax.value.amount;
      };

    case "percent":
      return (price, priceWithoutOption) => {
        return price + priceWithoutOption * (extraCost.rate / 100);
      };

    default:
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `Unsupported custom option price calculation type "${extraCost.type}". Please ensure your options are properly configured.`
        );
      }
      return samePrice;
  }
};

const getPriceAdjusterFromSelectedValue =
  (customOptions, selectedOptions) => (selectedOptionId) => {
    const option = customOptions.find(
      ({ option_id }) => option_id === selectedOptionId
    );

    if (option) {
      if (isDirectInput(option)) {
        return makePriceAdjuster(option);
      }

      const value = option.values.find(
        ({ option_value_id }) =>
          option_value_id === selectedOptions[selectedOptionId]
      );
      if (value) {
        return makePriceAdjuster(value);
      }
    }

    return samePrice;
  };

const minimalRequiredOptionPriceAdjusters = (options) => {
  const getOptionPriceAdjuster = (option) => {
    if (isDirectInput(option)) {
      return makePriceAdjuster(option);
    }

    const adjusters = option.values.map(makePriceAdjuster);
    return (...args) => {
      const possiblePrices = adjusters.map((adjustPrice) =>
        adjustPrice(...args)
      );
      return Math.min(...possiblePrices);
    };
  };
  return options
    .filter(({ is_required }) => is_required)
    .map(getOptionPriceAdjuster);
};

export const fusePriceWithCustomOptions = (
  product,
  selectedCustomOptions,
  getPrice
) => {
  const productPrice = getPrice(product.prices);
  if (productPrice === undefined || productPrice === null) {
    return null;
  }

  const selectedOptionIds = Object.keys(selectedCustomOptions);
  const notSelected = (option) => !selectedOptionIds.includes(option.option_id);
  const priceAdjusters = selectedOptionIds
    .map(
      getPriceAdjusterFromSelectedValue(
        product.custom_options,
        selectedCustomOptions
      )
    )
    .concat(
      minimalRequiredOptionPriceAdjusters(
        product.custom_options.filter(notSelected)
      )
    );

  return priceAdjusters.reduce(
    (price, adjustPrice) => adjustPrice(price, productPrice),
    productPrice
  );
};

const withSelectedCustomOptions = () =>
  compose(
    withState(
      "selectedCustomOptions",
      "setSelectedCustomOptions",
      (props) => props.selectedCustomOptions ?? {}
    ),
    withHandlers({
      setCustomOption: setOptionHandler("setSelectedCustomOptions"),
    }),
    withProps((props) => {
      if (
        !props.product.custom_options ||
        props.product.custom_options.length === 0
      ) {
        return {};
      }

      const prices = computeUpdatedPricesForProduct(
        props.product,
        props.selectedCustomOptions,
        fusePriceWithCustomOptions
      );

      return {
        product: {
          ...props.product,
          prices,
        },
      };
    })
  );

export default withSelectedCustomOptions;
